// src/ui/screens/verifications/RejectedVerificationPage.jsx

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import { db } from '../../../firebase';
import { doc, getDoc } from 'firebase/firestore';

const COLORS = {
  orange: '#FF8C08',
  bg: '#08090B',
  card: '#23262C',
};

// ── RejectedVerificationPage ─────────────────────────────────────────────────

export const RejectedVerificationPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [rejectionReason, setRejectionReason] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // ── Get rejection reason ──────────────────────────────────────────────────

  useEffect(() => {
    const getRejectionReason = async () => {
      if (!user) {
        setIsLoading(false);
        return;
      }

      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          const verification = data.verification;
          if (verification && verification.rejectionReason) {
            setRejectionReason(verification.rejectionReason);
          }
        }
      } catch (error) {
        console.error('Error fetching rejection reason:', error);
      } finally {
        setIsLoading(false);
      }
    };

    getRejectionReason();
  }, [user]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleSubmitNew = () => {
    navigate('/verifications/id');
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-filatom-bg">
      {/* Top Bar */}
      <div className="pt-safe-top px-3 pb-1">
        <div className="flex items-center">
          <button
            onClick={handleGoBack}
            className="w-10 h-10 rounded-full flex items-center justify-center border border-white/10"
            style={{ backgroundColor: COLORS.card }}
          >
            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex items-center justify-center px-6">
        <div className="flex flex-col items-center max-w-sm w-full">
          {/* Icon */}
          <div
            className="w-25 h-25 rounded-full flex items-center justify-center"
            style={{ backgroundColor: 'rgba(255,23,68,0.12)' }}
          >
            <svg className="w-12 h-12 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>

          <h2 className="text-white text-2xl font-bold mt-6 text-center">
            Verification Rejected
          </h2>

          <p className="text-white/70 text-sm text-center mt-3 leading-relaxed">
            Your seller verification was not approved.
          </p>

          {/* Rejection Reason */}
          {!isLoading && rejectionReason && (
            <div
              className="w-full mt-4 p-4 rounded-xl border"
              style={{
                backgroundColor: 'rgba(255,23,68,0.08)',
                borderColor: 'rgba(255,23,68,0.3)',
              }}
            >
              <p className="text-red-500 text-xs font-semibold">Reason:</p>
              <p className="text-white/80 text-xs mt-1.5 leading-relaxed">
                {rejectionReason}
              </p>
            </div>
          )}

          {/* Submit New Button */}
          <button
            onClick={handleSubmitNew}
            className="w-full h-12 mt-6 rounded-xl bg-filatom-primary text-black font-semibold text-sm"
          >
            Submit New Verification
          </button>
        </div>
      </div>
    </div>
  );
};

export default RejectedVerificationPage;